import theme from 'stories/utils/theme'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import Row from 'components/elements/Row'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import Stack from 'components/elements/Stack'
import ViewsIcon from 'assets/icons/webapp_blue_views.svg'
import { formatLocaleDate } from 'lib/formatting/datetime'
import { Caption, Body2, Body1 } from 'stories/elements/Typography/Text'
import { parseLocale, localizeNumber } from 'lib/constants/localization'
import ProfilePicture from 'components/elements/ProfilePicture'
import { KnowerLink } from 'lib/features/Know/knower'
import OverhaulKnowSEOBreadcrumbs from '../overhaul/OverhaulKnowSEOBreadcrumbs'
import _useTranslation from 'lib/hooks/_useTranslation'
import HTMLArticleContent from 'components/feature/Article/HTMLArticleContent'
import { ActiveExperiments } from 'lib/features/activeExperiments'
import { CourseBadge, SubjectBadge } from 'components/elements/Badge'
import { useState, useEffect } from 'react'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventKnowSummaryExpandClick } from 'lib/constants/AnalyticsEventType'
import AppStylePreview from '../AiEnhanced/AppStylePreview'
import NewButton from 'components/elements/NewButton'
import { truncateHtmlByWords } from 'lib/formatting/truncatingHtml'
import { RevampArticleStyles } from 'components/feature/Article/RevampArticle.styles'
import { returnKnowPagesLabel } from 'lib/formatting/knows'

const Container = styled.div<{
    isDarkTheme?: boolean
}>`
    height: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: visible;
    padding: 15px 0 0 0;

    @media (max-width: 1050px) {
        padding: 20px;
    }

    h1 {
        font-family: var(--font-inter) !important;
        font-size: 40px;
        line-height: 44px;
        font-weight: 650;
        letter-spacing: -0.8px;
        color: ${theme.colors.white};
        margin: 0;
        margin-bottom: 0px;
        text-align: left;
        max-width: 950px;
        @media (max-width: 1050px) {
            width: 100%;
            font-size: 24px;
            line-height: 32px;
        }
    }
    .breadcrumbs {
        padding-bottom: 5px;
        @media (max-width: 1050px) {
            padding: 0 0 5px 0;
        }
    }
    .know-actions {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        max-width: 1050px;
        padding: 0 10px;

        @media (max-width: 1050px) {
            padding: 0px;
        }
        @media (max-width: 400px) {
            padding: 0px;
        }
    }
    .stats {
        p {
            font-family: var(--font-inter);
            font-size: 13px;
            line-height: 16px;
            letter-spacing: 0.5px;
            font-weight: 400;
            color: ${theme.colors.grayScaleSecondary};
        }
        .pages-count {
            color: ${theme.colors.black};
            font-weight: 550;
        }
        .pages-icon-container {
            display: flex;
            align-items: center;
            gap: 4px;
            background-color: ${theme.colors.lightGrayBackground};
            height: 28px;
            border-radius: ${theme.borderRadius.rounded};
            padding: 0 12px;
            p {
                color: black;
                font-weight: 450;
            }
        }

        flex-wrap: wrap;
        path {
            fill: ${theme.colors.primaryGray};
        }
        .heart {
            path {
                stroke: ${theme.colors.primaryGray};
            }
        }
        .pages-icon path {
            fill: none;
        }
        span {
            padding: 0;
        }
    }
    .hero {
        width: 1050px;
        display: flex;
        justify-content: center;
        gap: 50px;
        @media (max-width: 1050px) {
            width: 100%;
            flex-direction: column;
        }
    }
`

const Separator = styled.div`
    height: 2px;
    width: 100%;
    position: relative;
    bottom: 10px;
    border-radius: 100px;
    background: linear-gradient(270deg, rgba(139, 189, 255, 0.4) 4.61%, rgba(75, 165, 255, 0.4) 100%);
`

const HeroContent = styled.div<{ isMobile?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 20px;
    justify-content: center;
    min-height: ${({ isMobile }) => (isMobile ? '150px' : '200px')};
    width: 100%;

    .knower-stats {
        min-height: 40px;
        display: flex;
        gap: 15px;
        justify-content: start;
        align-items: center;
        flex-wrap: wrap;
        button div {
            border: none;
        }
        .follower-count {
            white-space: nowrap;
        }
        .knower-name {
            flex-direction: column;
            gap: 0;
            p:first-child {
                line-height: 16px;
            }
        }
    }
`

const SummaryContentWrapper = styled.div<{ isMobile?: boolean; isDarkTheme?: boolean }>`
    width: 100%;
    position: relative;
`

const StyledCaption = styled(Caption)`
    font-family: var(--font-inter);
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.5px;
    font-weight: 400;
    color: ${theme.colors.grayScaleSecondary};
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    onContentClick: () => void
    referrerScreen?: string
    isMobile?: boolean
    isDarkTheme?: boolean
    experiments: ActiveExperiments
}

const AiEnhancedPNGHeroSection = ({
    knowSeoInfo,
    onContentClick,
    referrerScreen,
    isMobile,
    isDarkTheme,
    experiments,
}: Props) => {
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const router = useRouter()
    const parsedLocale = parseLocale(router.locale)
    const { know } = knowSeoInfo
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setIsExpanded(false)
    }, [knowSeoInfo])

    const truncatedContent = knowSeoInfo.summary ? truncateHtmlByWords(knowSeoInfo.summary, 40) : null

    const KnowerSection = () => (
        <KnowerLink knower={know.knower}>
            <div className="knower-stats">
                <Row>
                    <ProfilePicture
                        user={know.knower.user}
                        size={35}
                        hasBorder={!isDarkTheme}
                        borderColor={theme.colors.backgroundBasic}
                    />
                    <Stack gutter={0} className="knower-name">
                        <Body2
                            fontSize={15}
                            color={theme.colors.lightGrayBackground}
                            fontFamily="var(--font-inter)"
                            fontWeight={550}
                        >
                            {know.knower.user.name ?? know.knower.user.username}
                        </Body2>
                        <Body2
                            fontSize={13}
                            color={theme.colors.grayScaleSecondary}
                            fontFamily="var(--font-inter)"
                            fontWeight={400}
                        >
                            {`@${know.knower.user.username}`}
                        </Body2>
                    </Stack>
                </Row>
                <Row gap={10}>
                    <NewButton
                        borderRadius={theme.borderRadius.rounded}
                        height={36}
                        color={theme.colors.backgroundTransparent}
                    >
                        <Body2 color={theme.colors.white} fontWeight={450} fontFamily="var(--font-inter)">
                            {t('knows/knowDetailsFollowKnowerButton')}
                        </Body2>
                    </NewButton>
                </Row>
            </div>
        </KnowerLink>
    )

    const onExpandClick = () => {
        setIsExpanded(true)
        trackEvent(AnalyticsEventKnowSummaryExpandClick, { know })
    }

    const renderSummaryContent = () => {
        if (!knowSeoInfo.summary) {
            return (
                <RevampArticleStyles>
                    <Body1 fontFamily="var(--font-inter)" fontWeight={400}>
                        {knowSeoInfo.description}
                    </Body1>
                </RevampArticleStyles>
            )
        }

        if (!truncatedContent) {
            return null
        }

        if (isExpanded || !truncatedContent.isTruncated) {
            return <HTMLArticleContent contentHtml={knowSeoInfo.summary} isDarkTheme={isDarkTheme} useRevamp />
        }

        const truncatedWithButton =
            truncatedContent.truncated +
            `... <span style="color: ${
                theme.colors.accentBlueContrast
            }; font-family: var(--font-inter); font-weight: 550; font-size: 17px; line-height: 27px; letter-spacing: 0.3px; cursor: pointer; text-decoration: none;" onclick="this.parentElement.setAttribute('data-expand-clicked', 'true')" onmouseout="this.style.textDecoration='none'">${tCommon(
                'common/showMoreButtonText'
            )}</span>`

        return (
            <div
                onClick={(e) => {
                    if (
                        (e.target as HTMLElement).getAttribute('data-expand-clicked') === 'true' ||
                        (e.target as HTMLElement).tagName === 'SPAN'
                    ) {
                        onExpandClick()
                    }
                }}
            >
                <HTMLArticleContent contentHtml={truncatedWithButton} isDarkTheme={isDarkTheme} useRevamp />
            </div>
        )
    }

    return (
        <Container isDarkTheme={isDarkTheme}>
            <section className="know-actions" data-nosnippet="true">
                <div className="hero">
                    <HeroContent isMobile={isMobile}>
                        {isMobile ? (
                            <>
                                <AppStylePreview knowSeoInfo={knowSeoInfo} onContentClick={onContentClick} />

                                <OverhaulKnowSEOBreadcrumbs
                                    knowSeoInfo={knowSeoInfo}
                                    referrerScreen={referrerScreen}
                                    minimalizeBreadCrumbs
                                    showArrows
                                />
                                <Stack gutter={10}>
                                    <h1>{knowSeoInfo.title}</h1>
                                </Stack>
                            </>
                        ) : null}
                        {!isMobile ? (
                            <>
                                <OverhaulKnowSEOBreadcrumbs
                                    knowSeoInfo={knowSeoInfo}
                                    referrerScreen={referrerScreen}
                                    minimalizeBreadCrumbs
                                    showArrows
                                />
                                <Row gap={10} className="stats">
                                    {isDarkTheme && !isMobile ? (
                                        <>
                                            {know.subjects?.length ? (
                                                <>
                                                    <SubjectBadge subject={know.subjects[0]} isTransparent />
                                                    <StyledCaption>•</StyledCaption>
                                                </>
                                            ) : null}
                                            {know.courses?.length ? (
                                                <>
                                                    <CourseBadge course={know.courses[0]} isTransparent />
                                                    <StyledCaption>•</StyledCaption>
                                                </>
                                            ) : null}
                                        </>
                                    ) : null}

                                    <Row gap={8}>
                                        <ViewsIcon />
                                        <StyledCaption
                                            color={theme.colors.actionGray}
                                            fontFamily="var(--font-inter)"
                                            fontWeight={500}
                                        >
                                            {localizeNumber(knowSeoInfo.know.views ?? 0, parsedLocale.interfaceLanguageCode)}
                                        </StyledCaption>
                                    </Row>
                                    <StyledCaption>•</StyledCaption>
                                    <Row gap={8}>
                                        <StyledCaption
                                            color={theme.colors.actionGray}
                                            fontFamily="var(--font-inter)"
                                            fontSize={14}
                                            fontWeight={500}
                                        >
                                            {formatLocaleDate(
                                                knowSeoInfo.know.createdOn,
                                                parsedLocale.interfaceLanguageCode
                                            )}
                                        </StyledCaption>
                                    </Row>
                                    <StyledCaption>•</StyledCaption>

                                    <div className="pages-icon-container">
                                        <StyledCaption
                                            color={theme.colors.actionGray}
                                            fontFamily="var(--font-inter)"
                                            fontWeight={500}
                                        >
                                            {returnKnowPagesLabel(
                                                know.knowDocumentPages.length,
                                                t,
                                                parsedLocale.interfaceLanguageCode
                                            )}
                                        </StyledCaption>
                                    </div>
                                </Row>
                                <Stack gutter={10}>
                                    <h1>{knowSeoInfo.title}</h1>
                                </Stack>
                            </>
                        ) : null}

                        {!isMobile ? <KnowerSection /> : null}
                        <Separator />

                        <Stack gutter={15} className="summary-content">
                            <SummaryContentWrapper isMobile={isMobile} isDarkTheme={isDarkTheme}>
                                {renderSummaryContent()}
                            </SummaryContentWrapper>
                        </Stack>
                    </HeroContent>
                </div>
            </section>
        </Container>
    )
}

export default AiEnhancedPNGHeroSection
